import { Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, of } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { BlockBuilderComponent } from '@/components/block-builder/block-builder.component';
import { SpinnerComponent } from '@/components/shared/spinner/spinner.component';
import { ReportsService } from '../../reports/reports.service';
import { ReportMetadataBlocksService } from '../report-metadata-blocks.service';
import { getReportRequestDetailsFormObject } from '../report-request-details/utils/report-request-details-form-object';
import { getReportRequestPVItems } from '../report-request-details/utils/report-request-pv-items';
import { ReportTaskDetailsDTO, PpmOpinion } from '@/types';
import { DynamicForm } from '@/components/form-builder/form-builder.types';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { ButtonModule } from 'primeng/button';
import { FileService, NAMESPACE_ENUM } from '@/components/shared/file-list/file-list.service';
import { StatusComponent } from '@/components/shared/status/status.component';

@Component({
  selector: 'sm-report-inspect-content-block',
  standalone: true,
  imports: [CommonModule, FormBuilderComponent, BlockBuilderComponent, SpinnerComponent, TranslateModule, ButtonModule, StatusComponent],
  template: `
    <sm-spinner [loading]="loading$"></sm-spinner>
    <div class="flex flex-row gap-5" *ngIf="!(loading$ | async) && formObject">
      <div class="flex flex-col gap-4 w-3/4">
        <div class="content" *ngIf="metadataBlocks.length > 0">
          <div class="h-fit flex flex-col gap-4" *ngFor="let blocks of metadataBlocks">
            <sm-block-builder [blocks]="blocks"></sm-block-builder>
          </div>
        </div>
        <div class="content">
          <sm-form-builder [formObject]="formObject"></sm-form-builder>
        </div>
      </div>

      <div class="flex flex-col gap-2 w-1/4">
        <sm-status [status]="'clarification.status.' + status" [extra]="etapeProcess"></sm-status>
      </div>
    </div>
  `,
  styles: [`
    .content {
      @apply bg-white p-6 rounded-xl shadow-sm border border-gray-100;
    }
  `]
})
export class ReportInspectContentBlockComponent implements OnInit {
  private reportsService = inject(ReportsService);
  private metadataBlocksService = inject(ReportMetadataBlocksService);
  private translate = inject(TranslateService);
  private fileService = inject(FileService);

  private _id?: number;
  @Input() set id(value: number | undefined) {
    this._id = value;
    this.tryLoadData();
  }
  get id(): number | undefined {
    return this._id;
  }

  private _version?: number;
  @Input() set version(value: number | undefined) {
    this._version = value;
    this.tryLoadData();
  }
  get version(): number | undefined {
    return this._version;
  }

  loading$ = new BehaviorSubject<boolean>(true);
  formObject?: DynamicForm;
  metadataBlocks: DynamicBlocks[] = [];
  controlBlocks: DynamicBlocks[] = [];
  hasAvisRapporteur = false;
  rapporteurAlfrescoId?: string;
  status = '';
  etapeProcess = '';

  ngOnInit(): void {
    this.tryLoadData();
  }

  private tryLoadData() {
    if (this._id) {
      this.loadData();
    }
  }

  loadData() {
    this.loading$.next(true);
    this.reportsService.getTaskDetailsByReportId(this._id!).subscribe({
      next: (data: ReportTaskDetailsDTO) => {
        this.status = data.status;
        this.etapeProcess = data.taskName;
        this.setupDetails(data);
        this.loading$.next(false);
      },
      error: () => {
        // Handle error if task details not found, try getting report directly
        this.reportsService.getReportById(this._id!).subscribe({
          next: (report) => {
            this.status = report.status || 'BROUILLON';
            this.etapeProcess = '';
            const fakeDetails: ReportTaskDetailsDTO = {
              taskId: '',
              taskName: '',
              taskDefinitionKey: '',
              assignee: '',
              processInstanceId: '',
              candidateGroup: '',
              comId: 0,
              report: {
                id: report.id,
                reference: report.reference,
                datereport: report.dateReport,
                motifreport: report.motifReport,
                dacReference: report.dacReference,
                status: report.status || 'BROUILLON',
                sourceDemande: report.sourceDemande,
                modelDoc: report.modelDoc
              },
              status: report.status || 'BROUILLON',
              traceability: null,
              isUpdatable: false,
              isPublishable: false,
              commissionRequired: false,
              meetingRequired: false,
              assignRapporteur: false,
              hasTask: false,
              nextSteps: [],
              avis: [],
              pvCpmp: [],
              pvCncmpPermanente: [],
              pvCncmpSpecialisee: [],
              extraitCncmpPermanente: [],
              extraitCpmp: [],
              documentCNCMPSpecialisee: [],
              documentCNCMPPermanente: [],
              documentCPMP: [],
            };
            this.setupDetails(fakeDetails);
            this.loading$.next(false);
          },
          error: () => {
            this.loading$.next(false);
          }
        });
      },
    });
  }

  setupDetails(data: ReportTaskDetailsDTO) {
    const updatedDate$ = new BehaviorSubject<Date | null>(data.report.datereport ? new Date(data.report.datereport) : null);
    const updatedMotif$ = new BehaviorSubject<string>(data.report.motifreport || '');
    const updatedSourceDemande$ = new BehaviorSubject<string>(data.report.sourceDemande || '');
    const dateError$ = new BehaviorSubject<string | null>(null);
    const motifError$ = of(null);
    const sourceDemandeError$ = of(null);

    this.formObject = getReportRequestDetailsFormObject(
      data,
      false, // not draft
      updatedDate$,
      updatedMotif$,
      updatedSourceDemande$,
      dateError$,
      motifError$,
      sourceDemandeError$,
      (key: string) => this.translate.instant(key)
    );

    this.metadataBlocks = this.metadataBlocksService.getMetadataBlocks({
      pvItems: getReportRequestPVItems(data),
      ppmOpinions: data.avis
    });

    const rapporteurAvis = data.avis?.find((a: PpmOpinion) => !!a.alfrescoId);
    if (rapporteurAvis) {
      this.hasAvisRapporteur = true;
      this.rapporteurAlfrescoId = rapporteurAvis.alfrescoId;
    }
  }

}
